import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import globals from 'scoreboard/components/globals'
import SeriePeriodsForm from 'scoreboard/charts/edit/serie/serie-periods-form'
import ChartDesignerSerie, { PeriodCollection }
	from 'scoreboard/charts/model/chart-designer-serie'
import DataSourceSelector from 'scoreboard/components/data-source-selector/data-source-selector'
import metadata from 'scoreboard/components/metadata'
import unwrapModel from 'scoreboard/components/unwrap-model'
import Tooltip from 'scoreboard/components/tooltip'
import isGeographicDataType from 'scoreboard/components/is-geographic-data-type'
import settingsHelper from 'scoreboard/charts/edit/settings-helper'
import enums from 'scoreboard/components/enums'
import * as dataProperty from '../../data-property'
import ensureSeriePeriods from './ensure-serie-periods'
import plotTypes from './plot-types'
import plotTypeOptionTemplate from './plot-type-option.hbs'
import plotTypeTopLevelViewTemplate from './plot-type-top-level-view.hbs'
import formatValue from 'scoreboard/components/format-value'
import ForecastModelView from './forecast-model-view'
import testScorecardItemSerie from './test-scorecard-item-serie'
import adjustBreakdownDatasetFields from './adjust-breakdown-dataset-fields'
import BootslideEditMenu from '../bootslide-edit-menu'
import { chart as isPolarChart } from 'scoreboard/charts/is-polar'
import { ColorDisabledMenuOption, isColorOptionDisabled } from './color-disabled-menu-option'
import DataTypeFieldSelector from './data-type-field-selector'
import createScorecardBreakdown from './create-scorecard-breakdown'
import isSerieInErrorState from 'scoreboard/charts/is-serie-in-error-state'
import ChartDesignerFeature from '../../model/chart-designer-feature'
const { isSerieFieldByIdOrTempId } = ChartDesignerFeature
import findByIdsFactory from 'scoreboard/charts/model/find-by-ids-factory'
const findByIds = findByIdsFactory()
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import GeographicMapLevel from 'scoreboard/charts/model/geographic-map-level'
const { EARTH3D, EARTH2D, CONTINENT, COUNTRY } = GeographicMapLevel
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT, ROLLUP, POSTAL_CODE } = DataType
import { axisDataTypeInfo } from 'scoreboard/dashboards/widgets/chart/config/y-axis-range-config'
import { enableLevelsBetween, getLevelIndices } from 'scoreboard/charts/edit/map-levels-menu'
import { ColorTypes, DEFAULT_COLOR_TYPE } from 'scoreboard/charts/color-types'

// import ChartDesigner so that IntelliJ fully understands what the model is
import {
	adjustYAxisSettings
} from 'scoreboard/charts/model/chart-designer'

/**
 * @extends BootslideEditMenu
 */
const EditSerieMenu = BootslideEditMenu.extend(/** @lends EditSerieMenu */{

	/** @type {ChartDesigner} */
	model: null,

	initialize: function (options = {}) {
		BootslideEditMenu.prototype.initialize.apply(this, arguments)
		this.serie = this.feature
	},

	/**
	 * @param {Array} topLevel
	 */
	addTopLevelOptions: function(topLevel) {
		const isPlaceholder = this.serie.get('placeholderSerie')
		const plotType = this.serie.get('plotType')

		topLevel.push(this.createEditLabelOption({
			doneKey: 'common.done',
			defaultLabel: this.model.featureLabel({ feature: this.feature }),
			isPlaceholder,
			inputLabel: i18n.get('charts.series.inputLabel', this.typeLabel()),
			header: 'charts.series.setLabel',
			optionLabel: 'charts.series.setLabel',
			toggleLabel: 'charts.series.labelType'
		}))

		topLevel.push(this.dataSourceOption())

		if (this._includePeriodsOption()) {
			topLevel.push({
				label: 'charts.setPeriod',
				target: this._seriePeriodForms.bind(this)
			})
		}

		if (this.isTreeMap()) {
			topLevel.push(this.rollupFieldMenu())
		}

		if (this.isGeographicMap()) {
			topLevel.push(this.geographicFieldMenu())
		}

		if (!this.isGeographicMap()) {
			topLevel.push(this.createColorMenuOrDisabledColorOption())
		}

		const allowedTypes = this._allowedPlotTypes(plotType)
		const hasParent = !_.isNil(this.serie.get('parentId'))
			|| !_.isNil(this.serie.get('tempParentId'))
		if (allowedTypes.length > 1 && !hasParent) {
			topLevel.push(this.plotTypeMenu(allowedTypes, this.showForecastMenu.bind(this)))
		}
		if (isPolarChart(this.model)) {
			topLevel.push(fillRingToggleOption(this.feature))
		}

		topLevel.push(this.forecastMenu())

		if (!this.model.isHierarchical()) {
			topLevel.push(this.createCopyOption(this.serie, ChartDesignerSerie))
			topLevel.push(this.createDeleteOption())
		}
	},

	_allowedPlotTypes: function(plotType) {
		const type = plotTypes().find(p => p.type === plotType)
		if (!type) {
			// #39240 If the plot type is null, an error will be thrown anyway, and
			// needs to be. This is just so we have a more descriptive error in case
			// someone needs to fix this besides me
			throw new Error('The chart designer is missing a plot type. Cannot add new data to chart')
		}
		return type.canAdd
	},

	createColorMenuOrDisabledColorOption: function() {
		if (isColorOptionDisabled(this.model, this.serie)) {
			// #37638 display a stub option that only shows an info tooltip
			return {
				label: 'charts.chart.color',
				contentView: new ColorDisabledMenuOption({
					model: this.model,
					serie: this.serie
				})
			}
		}
		// #38914 we pass true to remove white as it's not a valid color options
		// for charts series
		return this.createColorMenu(true)
	},

	_includePeriodsOption: function() {
		if (this.serie.get('source').domainType === 'dataset') {
			// Set Period isn't used for dataset series; dataset filters are
			// directly added to the data source instead
			return false
		}

		return !_.isEmpty(this.serie.periods) && this._containsPeriodGroupBreakdowns()
	},

	_containsPeriodGroupBreakdowns: function() {
		return this.model.breakdowns
			.filter(it => it.isCalendarPeriodGroupBreakdown())
			.length > 0
	},

	_seriePeriodForms: function() {
		const periodsCopy =
			new PeriodCollection(this.serie.periods.map(p => p.clone()))
		var seriePeriodsForm = new SeriePeriodsForm({
			collection: periodsCopy,
			chart: this.model
		})

		seriePeriodsForm.once('done', () => {
			this.serie.set('periods', periodsCopy.models.map(it => unwrapModel(it)))
		})

		appDisplay.dialog(seriePeriodsForm)

		this.trigger('done')
	},

	plotTypeMenu: function (allowedTypes, plotTypeChangeHandler) {
		let targets = plotTypes()
			.filter(p => allowedTypes.includes(p.type))
			.map(type => {
				return {
					label: type.label,
					contentView: new PlotTypeOption({
						plotType: type.type,
						icon: type.icon,
						label: type.label,
						model: this.serie,
						chart: this.model
					})
				}
			})

		const label = this.isGeographicMap() ? i18n.get('charts.mapType') :
			i18n.get('dashboards.graphType')
		return {
			label,
			contentView: new PlotTypeTopLevelView({
				model: this.serie,
				label,
				plotTypeChangeHandler
			}),
			target: targets
		}
	},

	/**
	 * #37548 only show the rollup field option for tree maps
	 */
	showRollupFieldMenu: function() {
		this.bootslide.toggleItem('rollup-field', this.isTreeMap()
			&& this.serie.get('source').domainType === 'dataset')
	},

	showGeographicFieldMenu: function() {
		this.bootslide.toggleItem('geographic-field', this.isGeographicMap()
			&& this.serie.get('source').domainType === 'dataset')
	},

	/**
	 * #36721 Only display the forecast menu when the plot type is "line" and the
	 * primary breakdown is by a calendar field
	 */
	showForecastMenu: function() {
		const breakdowns = this.model.breakdowns.toArray()
		const show = this.serie.get('plotType') === 'line' && breakdowns.length
			&& breakdowns[0].isCalendarPeriodBreakdown()
		this.bootslide.toggleItem('forecast-menu', show)
	},

	/**
	 * #36721 Only display the forecast menu options when the "Show Forecast"
	 * toggle is turned on
	 */
	updateForecastOptionsVisibility: function() {
		const showOptions = !!this.serie.forecastModel.get('forecastType')
		this.bootslide.toggleItem('set-forecast-model', showOptions)
		this.bootslide.toggleItem('confidence-interval', showOptions)
		if (showOptions) {
			this.setDynamicWidth({
				target: $(this.currentTarget),
				updateMenuWidth: true
			})
		}
		this.$('#bootstrap-forecast-menu')
			.toggleClass('has-solo-visible-child', !showOptions)
	},

	render: function() {
		BootslideEditMenu.prototype.render.apply(this, arguments)
		// #36721 once the menu is rendered, make sure the forecast options are
		// displayed only as needed
		this.showForecastMenu()
		this.updateForecastOptionsVisibility()
		this.showRollupFieldMenu()
		this.showGeographicFieldMenu()
		return this
	},

	geographicFieldMenu: function() {
		const fieldIdAttr = 'datasetRollupFieldId'
		const linkedFieldIdAttr = 'datasetRollupVisibleLinkedFieldId'
		const viewCreator = () => {
			return new DataTypeFieldSelector({
				model: this.serie,
				datasets: this.model.data.datasets(),
				fieldType: 'geographic',
				fieldIdAttr,
				linkedFieldIdAttr
			})
		}

		const targetOption = BootslideMenu.targetOption('charts.chart.series.setGeographicField',
			viewCreator, results => {

			// if we are switching from a datatype that fits into the subdivisions
			// tree to one that fits into the postal codes tree, reset the
			// starting view selection for the map
			const initialDataType = _.get(results, 'initialField.dataType')
			const currentDataType = results.dataType
			if (initialDataType &&
				(initialDataType === POSTAL_CODE && currentDataType !== POSTAL_CODE)
				|| ((initialDataType !== POSTAL_CODE && currentDataType === POSTAL_CODE))) {
					this.model.unset('startingViewTreeId')
			}

			// turn off any map level settings that aren't valid after changing
			// the field and potentially data type
			const mapLevels = _.keys(enums.geographicMapLevel)
			const validMapLevels = settingsHelper.getValidMapLevels(results)
			_.each(mapLevels, type => {
				if (!_.includes(validMapLevels, type)) {
					this.model.set(`${_.camelCase(type)}Enabled`, false)
				}
			})

			// #53736 if all the enabled levels were invalid, then they were all
			// turned off. make sure at least one is turned on here
			enableFirstValidMapLevel({ model: this.model, validMapLevels })

			const { fieldId, visibleLinkedFieldId } = results
			this.serie.set(fieldIdAttr, fieldId)
			this.serie.set(linkedFieldIdAttr, visibleLinkedFieldId)

			// #53717 don't call removePlaceholder if we are just switching the
			// field
			if (_.isEmpty(results.initialField)) this.removePlaceholder()
			else toggleMapLevelsForFieldChange({
				designer: this.model,
				field: results,
				validMapLevels
			})

			this.trigger('datasource-updated')
			this.trigger('done')
		})

		// Add an id so we can show and hide this menu option
		const hasGeographicField = !!this.serie.get('datasetRollupFieldId')
		return _.extend(targetOption, { id: 'geographic-field',
			classNames: hasGeographicField ? '' : 'is-placeholder' })
	},

	rollupFieldMenu: function() {
		const fieldIdAttr = 'datasetRollupFieldId'
		const linkedFieldIdAttr = 'datasetRollupVisibleLinkedFieldId'
		const viewCreator = () => {
			return new DataTypeFieldSelector({
				model: this.serie,
				datasets: this.model.data.datasets(),
				fieldType: ROLLUP,
				fieldIdAttr,
				linkedFieldIdAttr
			})
		}

		const targetOption = BootslideMenu.targetOption('charts.chart.series.setRollupField',
			viewCreator, results => {
			const { fieldId, visibleLinkedFieldId } = results
			this.serie.set(fieldIdAttr, fieldId)
			this.serie.set(linkedFieldIdAttr, visibleLinkedFieldId)
			this.removePlaceholder()
			this.trigger('datasource-updated')
			this.trigger('done')
		})

		const hasRollup = !!this.serie.get('datasetRollupFieldId')
		// Add an id so we can show and hide this menu option
		return _.extend(targetOption, { id: 'rollup-field',
			classNames: hasRollup ? '' : 'is-placeholder' })
	},

	forecastMenu: function () {
		if (this._containsPeriodGroupBreakdowns()) {
			return this._disabledForecastMenuOption(arguments)
		}
		return this.forecastMenuOption()
	},

	_disabledForecastMenuOption: function () {
		const DisabledMenuOption = Backbone.View.extend({
			className: 'is-disabled',
			events: {
				'click': 'showTooltip'
			},

			render: function () {
				this.$el.empty().text(i18n.get('charts.forecast'))
				return this
			},

			showTooltip: function () {
				this.tooltip = new Tooltip({
					// attach to the parent so the tooltip shows completely
					// outside of the menu
					attachTo: this.$el.parent(),
					content: i18n.get('charts.forecast.groupingDisabled'),
					closeOnChangedFocus: true,
					closeOnAttachedToClick: true,
					placement: 'right',
					arrow: 'bottom'
				}).render()
				this.on('slide:away', () => _.invoke(this.tooltip, 'remove'))
			},

			remove: function () {
				Backbone.View.prototype.remove.apply(this, arguments)
				_.invokeMap([this.tooltip], 'remove')
			}
		})

		return { // #42390
			label: i18n.get('charts.forecast'),
			contentView: new DisabledMenuOption({
				model: this.model,
				serie: this.serie
			})
		}
	},

	forecastMenuOption: function () {
		const confidenceIntervals = [null, .90, .95, .99].map(value => ({
			contentView: new ConfidenceIntervalOption({
				value,
				model: this.serie.forecastModel,
				parentModel: this.serie
			})
		}))

		const menuOptions = [
			{
				contentView: new ToggleForecast({
					model: this.serie.forecastModel,
					onToggleForecastModel: this.onToggleForecastModel.bind(this)
				}),
				model: this.serie
			},
			{
				id: 'set-forecast-model',
				label: 'charts.forecast.setForecastModel',
				target: e => {
					e.preventDefault()

					let clonedForecastModel = this.serie.forecastModel.clone()

					const forecastModelView = new ForecastModelView({ model: clonedForecastModel })
					appDisplay.dialog(forecastModelView)

					this.listenToOnce(forecastModelView, 'done', results => {
						this.serie.forecastModel.set(results)
					})

					this.trigger('done')
				}
			},
			{
				id: 'confidence-interval',
				label: i18n.get('charts.forecast.confidenceInterval'),
				type: 'value-then-submenu',
				contentView: new ConfidenceIntervalTopLevelView({ model: this.serie }),
				model: this.serie,
				target: confidenceIntervals
			}
		]

		return {
			id: 'forecast-menu',
			label: i18n.get('charts.forecast'),
			type: 'value-then-submenu',
			model: this.serie,
			target: menuOptions
		}
	},

	onToggleForecastModel: function (showForecast) {
		if (showForecast) {
			// We need to make an XHR because the server needs to run a query to
			// possibly extend the breakdown ranges. To communicate this to the
			// user, put the chart in loading state
			this.trigger('loading')

			let data = {
				chartDesigner: this.model.toPostJSON(),
				serie: this.serie.toJSON()
			}

			$.ajax({
				method: 'POST',
				url: `api/charts/enable-forecast?calendarPeriodId=${globals.periodId}`,
				data: JSON.stringify(data),
				success: updatedDesigner => {
					// #44993 don't use this.model.set(updatedDesigner), because
					// if the serie is not yet persisted, Backbone.Model.set()
					// will remove the serie and then re-add it with the new data.
					// Instead, just update the one we enabled forecasting on.
					let updatedFeature = findByIds({
						id: this.serie.id,
						tempId: this.serie.get('tempId'),
						collection: updatedDesigner.features
					})
					this.serie.set(updatedFeature)

					// Charts with forecasting have exactly one breakdown, and
					// that breakdown could have been altered while enabling
					// forecasting (eg extending calendar period range)
					this.model.breakdowns.at(0).set(updatedDesigner.breakdowns[0])

					this.updateForecastOptionsVisibility()
				}
			})
		} else {
			// Intentionally only setting forecastType to null. This will effectively
			// turn it off, while remembering the other settings (like confidenceInterval)
			// in case the user turns it back on
			this.serie.forecastModel.set('forecastType', null)
			this.updateForecastOptionsVisibility()
		}
	},

	dataSourceOption: function () {

		const viewCreator = () => {
			let domainType = this.serie.get('source').domainType
			let dataSource = this.serie.getGenericDataSource()
			let plotType = this.serie.get('plotType')

			// Build array of options for the dataProperty dropdown
			let dataPropertyOptions = function ({ domainType, dataSource, domainObject }) {
				return dataProperty.options({ domainType, domainObject, plotType })
			}

			let datasetErrorCode
			const datasetId = dataSource.startingDatasetId
			if (datasetId) {
				const dataset = this.model.data.datasetModel(datasetId)
				datasetErrorCode = dataset.get('errorCode')
			}

			let requiredDataType = null
			if (this.isTreeMap()) requiredDataType = ROLLUP
			else if (this.model.isHierarchical()) requiredDataType = 'geographic'

			let selector = new DataSourceSelector({
				domainType,
				dataSource,
				dataPropertyOptions,
				defaultDataProperty: dataProperty.defaultValue,
				datasets: this.model.data.datasets(),
				scorecards: this.model.data.scorecards(),
				initiatives: this.model.data.initiatives(),
				treeItemsById: this.model.data.treeItems(),
				parentDomainType: 'chart',
				// https://github.com/SpiderStrategies/Scoreboard/issues/45540#issuecomment-1050099534
				useStartingDataset: false,
				allowAppCalendar: true,
				allowPercentageAggregationType: false,
				datasetsOnly: this.model.isHierarchical(),
				numericSumOnly: this.model.isHierarchical(), // #41781
				showAggregationType: this.model.isHierarchical() ? false : null, // #41781,
				allowCalculated: !this.model.isHierarchical(), // #41887
				allowListUniqueValues: false, // #59627
				datasetErrorCode,
				requiredDataType,
				defaultDatasetDateAggregationType: 'unique',
				objectSelectorOptions: {
					testSelectedObject: (selected, selectedDomainType) => {
						if (selectedDomainType != 'scorecard-item') {
							return
						}

						return testScorecardItemSerie(selected)
					}
				},
				includeEditFields: false
			})
			return selector
		}

		const oldDatasetId = _.get(this.serie.get('source'), 'aggregation.startingDatasetId')
		let target = BootslideMenu.targetOption('report.designer.setDataSource', viewCreator, results => {
			this.updateDataSource(results, oldDatasetId)
			this.trigger('done')
		})

		let classNames = ''

		// #40555 for tree maps, we can remove the styling from this menu option
		// after a dataset is selected, even if we are still showing placeholder data
		const { placeholderSerie, source } = this.serie.pick('plotType', 'placeholderSerie', 'source')
		const usePlaceholderStyle = (!this.model.isHierarchical() && placeholderSerie) ||
			(this.model.isHierarchical() && placeholderSerie && !source.aggregation)
		classNames += usePlaceholderStyle ? 'is-placeholder' : ''
		classNames += isSerieInErrorState(this.serie, this.model) ? 'is-serie-error-state' : ''
		if (!_.isEmpty(classNames)) {
			target = _.extend(target, { classNames })
		}

		return target
	},

	isGeographicMap: function() {
		const plotType = this.serie.get('plotType')
		return plotType === 'filled-map' || plotType === 'spike-map' ||
			plotType === 'bubble-map'
	},

	isTreeMap: function() {
		return this.serie.get('plotType') === 'tree-map'
	},

	/**
	 * Applies the results of DataSourceSelector to serie.source
	 */
	updateDataSource: function (results, oldDatasetId) {
		let source
		if (results.domainType === 'dataset') {
			// #50268 for calculated datasources, "results.dataset" will
			// be null, and wouldn't account for multiple datasets in the
			// equation anyway. So add everything from datasetsById to the
			// chart data
			_.toPairs(results.datasetsById).forEach(([id, dataset]) => {
				this.model.data.datasetModel(id, dataset)
			})

			source = {
				domainType: results.domainType,
				aggregation: results.dataSource
			}

			if (results.dataset) {
				this.model.data.datasets(results.dataset.id, unwrapModel(results.dataset))
				const { dataset } = results
				if (this.isTreeMap() && dataset.id !== oldDatasetId) {
					// #40352 The dataset has changed; if there is only 1 rollup
					// tree for this dataset, go ahead and select it
					const rollupFields = _.filter(dataset.combinedFields,
					o => isGeographicOrRollup(o.dataType))
					if (rollupFields.length === 1) {
						this.serie.set('datasetRollupFieldId', rollupFields[0].id)
					}
				}
				if (this.isGeographicMap() && dataset.id !== oldDatasetId) {
					const geographicFields = _.filter(dataset.combinedFields,
						o => isGeographicDataType(o.dataType))
					if (geographicFields.length === 1) {
						this.serie.set('datasetRollupFieldId', geographicFields[0].id)
					} else if (!_.isNil(oldDatasetId)) {
						this.serie.unset('datasetRollupFieldId')
					}
				}
			}
		} else {
			source = _.extend(
				{ domainType: results.domainType },
				_.omit(results.dataSource, 'domainObjectId', 'domainObject'))

			let section = metadata[results.domainType].section

			if (results.domainType === 'scorecard-item') {
				// Change generic property name to be what the server API uses
				source.scorecardItemId = results.dataSource.domainObjectId

				// Set dataProperty/thresholdIndex
				_.extend(source, dataProperty.splitDataPropertyOption(source.dataProperty))

				// #39856 Ensure that a breakdown is available
				createScorecardBreakdown(this.model)
			} else if (results.domainType === 'initiative') {
				source.initiativeId = results.dataSource.domainObjectId
			}

			// In case the scorecard/initiative data wasn't already loaded, add
			// it to the chart data
			this.model.data[section](results.dataSource.domainObjectId,
				results.dataSource.domainObject)
		}

		const oldSource = this.serie.get('source')
		if (equationHasChanged(oldSource.aggregation, source.aggregation)) {
			// #45808 if the equation has changed, remove any breakdown dataset
			// fields that referenced any of the old equation's aggregations
			const allDatasetIds = this.model.getAllDatasetIdsFromDataSource(source)
			this.model.breakdowns.forEach(b => {
				if (!b.isCalendarPeriodBreakdown()) {
					// #45978 we found a non-calendar breakdown; we need to
					// basically clear this out, but leave basic info, so it shows
					// in an error state
					b.resetToCalendarBreakdown()
				} else {
					const datasetFields = _.filter(b.get('datasetFields'), f => {
						// #45953 if the chart now has more than one dataset reference,
						// then remove the "same field" repeat by as it isn't valid anymore
						if (allDatasetIds.length > 1 && !f.serieId && !f.serieTempId) return false

						if (isSerieFieldByIdOrTempId(f, this.serie.get('id'), this.serie.get('tempId'))) return false

						// #45952 if the dataset that this field comes from is no
						// longer referenced in the serie equation, remove it
						if (!allDatasetIds.includes(f.datasetId)) return false

						return true
					})
					b.set({
						datasetFields,
						datasetFieldId: null
					})
				}
			})

		}

		const oldAxisInfo = axisDataTypeInfo(this.model, this.model.data)
		this.serie.set('source', source)
		const newAxisInfo = axisDataTypeInfo(this.model, this.model.data)

		adjustYAxisSettings(oldAxisInfo, newAxisInfo, this.model)

		if (results.wasPlaceholder) {
			// #38353 remove placeholder restrictions from the serie so it renders
			// correctly
			this.removePlaceholder()
		}

		// Make sure serie periods are properly initialized, in case it was a
		// dataset serie (no serie periods needed) and now is a scorecard or
		// initiative serie (serie periods needed)
		ensureSeriePeriods({ chartDesigner: this.model, newSerie: this.serie })

		this.model.removeInvalidBreakdowns()

		// #45349 may have switched from a non-dataset or placeholder serie to
		// a dataset serie; make any necessary adjustments (like auto-selecting
		// a date field)
		adjustBreakdownDatasetFields(this.model)

		if (!this.chartDesigner.supportsScorePointColor()) {
			_.each(this.chartDesigner.get('colors'), s => {
				if (s.colorType === ColorTypes.SCORE) {
					s.colorType = DEFAULT_COLOR_TYPE
				}
			})
		}

		this.trigger('datasource-updated')
	},

	removePlaceholder: function() {
		const { datasetRollupFieldId } = this.serie
			.pick('plotType', 'source', 'datasetRollupFieldId')
		const isHierarchicalPlotType = this.model.isHierarchical()
		if (!isHierarchicalPlotType || (isHierarchicalPlotType && datasetRollupFieldId)) {
			// #40555 For tree maps, only remove the need for the placeholder if the
			// rollup field is set
			this.serie.set('placeholderSerie', null)

			// #48820 when adding in real data for geographic maps, toggle on
			// all the valid levels except continent
			if (this.isGeographicMap()) setDefaultMapLevels({
				model: this.model,
				field: this.model.datasetGeographicField()
			})
		}

		// #50555 if the user has just set a custom label and is now setting
		// the source, keep it
		if (!this.serie.customLabel) this.serie.set('label', null)
	}
})

function equationHasChanged(oldAggregation, newAggregation) {
	// We aren't dealing with dataset sources
	if (!oldAggregation && !newAggregation) return false

	// The equation was newly added or removed
	if ((!oldAggregation && newAggregation) || (oldAggregation && !newAggregation)) return true

	// #46074 On a basic data source, we changed the dataset
	if (oldAggregation.startingDatasetId != newAggregation.startingDatasetId) return true

	// The number of aggregations changed
	if (_.get(oldAggregation, 'aggregations.length') != _.get(newAggregation, 'aggregations.length')) return true

	return _.some(newAggregation.aggregations, (agg, i) => {
		const oldAgg = oldAggregation.aggregations[i]
		// We only actually care if the dataset has changed. This would invalidate
		// any breakdown referencing this serie
		if (oldAgg.datasetId != agg.datasetId) return true

		return false
	})
}

/**
 * @param {ChartDesignerSerie} serie
 */
function fillRingToggleOption(serie) {
	return {
		contentView: new ToggleFillRing({
			model: serie
		}),
		model: serie
	}
}

var PlotTypeTopLevelView = Backbone.View.extend({
	initialize: function (opt) {
		this.options = opt
		this.listenTo(this.model, 'change:plotType', opt.plotTypeChangeHandler)
	},

	render: function () {
		let plotType = this.model.get('plotType')
		let type = _.find(plotTypes(), type => type.type === plotType)

		this.$el.html(plotTypeTopLevelViewTemplate({
			icon: type.icon,
			label: this.options.label || i18n.get('dashboards.graphType')
		}))

		return this
	}
})

const PlotTypeOption = BootslideMenu.MultipleChoiceOption.extend({
	property: 'plotType',

	initialize: function (opt) {
		this.value = opt.plotType

		BootslideMenu.MultipleChoiceOption.prototype.initialize.apply(this, arguments)

		this.label = $(plotTypeOptionTemplate({
			icon: opt.icon,
			label: opt.label
		}))[0]
	},

	/**
	 * @override
	 */
	updateModel: function (value) {
		this.options.chart.setSeriePlotType(this.model, value)
	}
})

const ToggleFillRing = BootslideMenu.ToggleProperty.extend({
	property: {
		label: 'charts.polar.fillRing',
		name: 'fillRing'
	}
})

const ToggleForecast = BootslideMenu.ToggleProperty.extend({
	/** @type {ChartDesignerForecast} */
	model: null,

	property: {
		label: 'charts.forecast.showForecast',
		name: 'forecastType'
	},

	initialize: function(options = {}) {
		BootslideMenu.ToggleProperty.prototype.initialize.apply(this, arguments)
		this.options = options
		this.value = this.model.get(this.property.name)
	},

	/**
	 * @override
	 */
	val: function () {
		// Don't read the model directly; we may be waiting on an XHR, but need
		// to update the state of the button immediately
		return this.value
	},

	/**
	 * @override
	 */
	updateModel: function(showForecast) {
		// Don't update the model directly; we may need to make an XHR to the
		// server first
		this.value = showForecast
		this._onChange()

		_.invoke(this.options, 'onToggleForecastModel', showForecast)
	}
})

const ConfidenceIntervalTopLevelView = BootslideMenu.MultipleChoiceMenu.extend({
	property: 'confidenceInterval',

	initialize: function() {
		BootslideMenu.MultipleChoiceMenu.prototype.initialize()
		this.label = i18n.get('charts.forecast.confidenceInterval')
	}

})

const ConfidenceIntervalOption = BootslideMenu.MultipleChoiceOption.extend({
	property: 'confidenceInterval',

	initialize: function (options = {}) {
		this.options = options
		this.value = options.value

		BootslideMenu.MultipleChoiceOption.prototype.initialize.apply(this, arguments)

		this.label = this.value ?
			formatValue({ value: this.value, dataType: PERCENT }) :
			i18n.get('common.none')
	},

	updateModel(value) {
		this.model.set(this.property, value)
	}
})

/**
 * Enables the first of the validMapLevels if none of them are enabled on the
 * chart designer model. No map levels is not a valid set up
 */
const enableFirstValidMapLevel = ({ model, validMapLevels }) => {
	if (!_.some(validMapLevels, ml => !!model.get(`${_.camelCase(ml)}Enabled`))) {
		model.set(`${_.camelCase(validMapLevels[0])}Enabled`, true)
	}
}

const setDefaultMapLevels = ({ model, field }) => {
	const mapLevels = _.keys(enums.geographicMapLevel)
	const validLevels = settingsHelper.getValidMapLevels(field)
	const sampleLevels = [EARTH3D, EARTH2D, CONTINENT, COUNTRY]
	_.each(mapLevels, level => {
		let enabled = _.includes(validLevels, level)
		if (!_.includes(sampleLevels, level) || !enabled) {
			model.set(`${_.camelCase(level)}Enabled`, enabled)
		}
	})

	// one level should be enabled at all times
	if (_.isEmpty(model.getEnabledMapLevels())) {
		model.set(`${_.camelCase(field.dataType)}Enabled`, true)
	}
}

const toggleMapLevelsForFieldChange = ({ designer, field, validMapLevels }) => {
	const { dataType } = field
	if (!designer.get(`${_.camelCase(dataType)}Enabled`)) {
		designer.set(`${_.camelCase(dataType)}Enabled`, true)

		const indices = getLevelIndices({ level: dataType, value: true },
			designer, field, validMapLevels)

		if (!_.isNil(indices)) {
			enableLevelsBetween(_.extend({}, {
				designer,
				validMapLevels
			}, indices))
		}
	}
}

export default EditSerieMenu
export { PlotTypeTopLevelView }
export { setDefaultMapLevels }
export { enableFirstValidMapLevel }
export { toggleMapLevelsForFieldChange }

EditSerieMenu.__filename__ = 'charts/edit/serie/edit-serie-menu.js'
